"use client";

import { get, set, del, update } from "idb-keyval";
import type { ReviewLanguage } from "@/lib/cv/review-language";

const STORAGE_KEY = "cv-upload";
export const CV_UPLOAD_DRAFT_TTL_MS = 15 * 60 * 1000;
/**
 * How long one page instance holds its claim on a draft. Long enough to cover
 * an upload of the largest CV we accept, short enough that a tab which died
 * mid-upload does not strand the draft for the rest of its TTL.
 */
export const CV_UPLOAD_CLAIM_TTL_MS = 2 * 60 * 1000;
/** How long a finished resume stays readable by other copies of the page. */
export const CV_UPLOAD_OUTCOME_TTL_MS = 2 * 60 * 1000;

export type TalentPoolUploadChoice = "yes" | "no";

export type StoredCV = {
  file: File;
  experienceLevel: string;
  reviewPurpose: "job" | "scholarship";
  targetJobTitle: string;
  programCampus: string;
  jobDescription: string;
  allowTalentPool: boolean;
  /**
   * The required pre-upload talent-pool answer. Optional only because drafts
   * stored before the question existed lack it.
   */
  talentPoolUploadChoice?: TalentPoolUploadChoice;
  /** Optional only because drafts stored before the language field lack it. */
  reviewLanguage?: ReviewLanguage;
  storedAt?: number;
  /** When some page instance took this draft to submit it. */
  claimedAt?: number;
};

/**
 * What the copy of the resume page that submitted the draft leaves behind, so
 * any other copy still on screen can follow it to the review or show the error.
 */
export type StoredCvOutcome =
  | { reviewRequestId: string }
  | { failureMessage: string };

type StoredOutcome = StoredCvOutcome & { completedAt: number };

type StoredValue = StoredCV | StoredOutcome | File | undefined;

export type CvDraftClaim =
  | { status: "claimed"; draft: StoredCV }
  /** Another copy holds a live claim and is still submitting. */
  | { status: "busy" }
  | { status: "completed"; reviewRequestId: string }
  | { status: "failed"; message: string }
  | { status: "none" };

const isLiveDraft = (value: StoredValue, now: number): value is StoredCV =>
  Boolean(
    value &&
    !(value instanceof File) &&
    "file" in value &&
    value.file instanceof File &&
    typeof value.storedAt === "number" &&
    now - value.storedAt <= CV_UPLOAD_DRAFT_TTL_MS,
  );

const isLiveOutcome = (value: StoredValue, now: number): value is StoredOutcome =>
  Boolean(
    value &&
    !(value instanceof File) &&
    "completedAt" in value &&
    typeof value.completedAt === "number" &&
    now - value.completedAt <= CV_UPLOAD_OUTCOME_TTL_MS &&
    ("reviewRequestId" in value || "failureMessage" in value),
  );

const toOutcomeClaim = (outcome: StoredOutcome): CvDraftClaim =>
  "reviewRequestId" in outcome
    ? { status: "completed", reviewRequestId: outcome.reviewRequestId }
    : { status: "failed", message: outcome.failureMessage };

/**
 * Takes ownership of the stored draft, handing it to exactly one caller.
 *
 * The OAuth resume page can run more than once for one submission: a second
 * tab of the same browser lands on it, or the page mounts again. Each instance
 * has its own refs, so the only thing they share is this store. idb-keyval's
 * update() is a single read-modify-write transaction, so exactly one caller
 * gets "claimed". The others get "busy" while that claim is live, and once the
 * claimer records its outcome they get "completed" or "failed", so every copy
 * ends on the same review instead of treating a claimed draft as no draft.
 *
 * A claim older than CV_UPLOAD_CLAIM_TTL_MS is treated as abandoned, so a tab
 * that was closed mid-upload does not keep the draft from ever being resumed.
 */
export const claimStoredCV = async (): Promise<CvDraftClaim> => {
  const now = Date.now();
  let claim: CvDraftClaim = { status: "none" };

  await update<StoredValue>(STORAGE_KEY, (stored) => {
    if (isLiveOutcome(stored, now)) {
      claim = toOutcomeClaim(stored);
      return stored;
    }

    // Expired, legacy or absent: drop it rather than leave it to be found.
    if (!isLiveDraft(stored, now)) {
      return undefined;
    }

    if (
      typeof stored.claimedAt === "number" &&
      now - stored.claimedAt <= CV_UPLOAD_CLAIM_TTL_MS
    ) {
      claim = { status: "busy" };
      return stored;
    }

    claim = { status: "claimed", draft: stored };
    return { ...stored, claimedAt: now };
  });

  return claim;
};

/** Replaces the claimed draft with how its submission ended. */
export const recordStoredCvOutcome = (outcome: StoredCvOutcome): Promise<void> =>
  set(STORAGE_KEY, { ...outcome, completedAt: Date.now() });

export const storeCV = (payload: StoredCV): Promise<void> => {
  return set(STORAGE_KEY, { ...payload, storedAt: Date.now() });
};

export const deleteStoredCV = (): Promise<void> => {
  return del(STORAGE_KEY);
};

// ── Talent-pool CV storage ───────────────────────────────
const TALENT_POOL_KEY = "talent-pool-cv";

export type StoredTalentPoolCV = {
  file: File;
};

export const getStoredTalentPoolCV = async (): Promise<
  StoredTalentPoolCV | undefined
> => {
  const stored = await get<StoredTalentPoolCV | File | undefined>(
    TALENT_POOL_KEY,
  );

  if (!stored) return undefined;
  if (stored instanceof File) return { file: stored };
  if ("file" in stored && stored.file instanceof File) return stored;

  return undefined;
};

export const storeTalentPoolCV = (payload: StoredTalentPoolCV): Promise<void> =>
  set(TALENT_POOL_KEY, payload);

export const deleteStoredTalentPoolCV = (): Promise<void> =>
  del(TALENT_POOL_KEY);
